// src/lib/breadcrumbNavigation.js
//
// Pure helper functions for managing the workspace breadcrumb context stack.
// Extracted from topLevelNav/index.js so they can be unit-tested in isolation.

/** Strip trailing slashes from a folder path. */
export const normWsFolder = (p) => (p ? String(p).replace(/\/+$/, "") : "");

/**
 * Derive the normalised folder path of a report's immediate parent folder,
 * or null for root-level (unfiled) reports.
 *
 * Resolution order:
 *  1. pathIds field on the report node — strip the report's own ID, take the
 *     deepest remaining ID, resolve it through wsIndex to get its .path.
 *  2. Fallback: derive from the report's own .path string by stripping the
 *     report name from the end.
 *
 * @param {object}      reportNode - report node from buildReportingIndex
 * @param {object|null} wsIndex    - { nodeById, nodeByPath } from buildReportingIndex
 * @returns {string|null}
 */
export const getWsParentFolderPath = (reportNode, wsIndex) => {
  if (!reportNode) return null;

  const reportKey = reportNode.id || reportNode._id || reportNode.rptId || "";
  const pathIds = reportNode.pathIds
    ? String(reportNode.pathIds)
      .split("/")
      .filter(Boolean)
      .filter((id) => id !== reportKey)
    : [];

  if (pathIds.length > 0) {
    const deepestFolderId = pathIds[pathIds.length - 1];
    const folderNode = wsIndex?.nodeById?.[deepestFolderId]?.node;
    if (folderNode?.path) return normWsFolder(folderNode.path);
  }

  // Fallback: derive from the report's path string.
  const rawPath = reportNode.path || reportNode.fullPath || "";
  if (!rawPath) return null;

  const segments = rawPath.replace(/\/+$/, "").split("/").filter(Boolean);
  const rptTitle = reportNode.title || reportNode.name || "";
  if (rptTitle && segments[segments.length - 1] === rptTitle) segments.pop();

  return segments.length > 0 ? segments.join("/") : null;
};

/**
 * Build the ordered list of ancestor folder paths to push, outermost first.
 *
 * segments[0] is the workspace-root name — it is not a crumb-worthy folder
 * itself, so we skip it.  For a root-level report whose parentFolderPath is
 * just the workspace name (one segment), this returns [] correctly.
 *
 * Examples:
 *   "WS"                  → []
 *   "WS/FolderA"          → ["WS/FolderA"]
 *   "WS/FolderA/SubA"     → ["WS/FolderA", "WS/FolderA/SubA"]
 *   "WS/FolderA/Sub/Deep" → ["WS/FolderA", "WS/FolderA/Sub", "WS/FolderA/Sub/Deep"]
 *
 * @param {string|null} parentFolderPath
 * @returns {string[]}
 */
export const buildAncestorFolderPaths = (parentFolderPath) => {
  if (!parentFolderPath) return [];
  const segs = parentFolderPath.split("/").filter(Boolean);
  // segs.slice(1) are the sub-folder segments below the workspace root.
  return segs.slice(1).map((_, i) => segs.slice(0, i + 2).join("/"));
};

/**
 * Resolve the canonical path-based ID to store for a folder in the BC stack.
 * We always use the decoded path (e.g. "Marketing/Q1") so that
 * renderBreadcrumbStack's labelFor / routeFor work via nodeByPath lookups.
 *
 * - Multi-segment values (contain "/") are returned as-is.
 * - Single-segment values are looked up first by nodeByPath, then nodeById.
 * - Falls back to returning the trimmed segment unchanged.
 *
 * @param {string}      rawSegment - folder path or id from the URL
 * @param {object|null} wsIndex    - { nodeById, nodeByPath } from buildReportingIndex
 * @returns {string}
 */
export const resolveWsFolderStackId = (rawSegment, wsIndex) => {
  if (!rawSegment) return "";
  const trimmed = normWsFolder(rawSegment);
  if (trimmed.includes("/")) return trimmed; // already a multi-segment path

  const byPath = wsIndex?.nodeByPath?.[trimmed]?.node;
  if (byPath?.path) return normWsFolder(byPath.path);

  const byId = wsIndex?.nodeById?.[trimmed]?.node;
  if (byId?.path) return normWsFolder(byId.path);

  return trimmed; // fallback: use as-is
};
